import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/** How long to keep the section in place while the page loads. */
const SETTLE_MS = 5000;
const SPACE_ABOVE_PX = 24;
const USER_SCROLL_EVENTS = ['wheel', 'touchmove', 'keydown', 'mousedown'];

/**
 * Scrolls to the element named by the URL hash, e.g. the card a ⌘K result
 * points at. Sections can render after their data loads, and content loading
 * above them can push them down, so this keeps the section in place until the
 * page settles or the user scrolls.
 */
export const useScrollToHash = () => {
  const { hash, key } = useLocation();

  useEffect(() => {
    // Section ids need no escaping, so the hash is used as is. Decoding it
    // would throw on a malformed fragment such as `#%`.
    const id = hash.slice(1);
    if (!id) return;

    const scrollToSection = () => {
      const element = document.getElementById(id);
      if (!element) return;
      // Keep the element clear of the sticky nav, which is taller in apps.
      const navHeight =
        document.querySelector('[data-sticky-nav]')?.getBoundingClientRect()
          .height ?? 0;
      window.scrollTo({
        top:
          element.getBoundingClientRect().top +
          window.scrollY -
          navHeight -
          SPACE_ABOVE_PX,
      });
    };

    const mutationObserver = new MutationObserver(scrollToSection);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    const resizeObserver = new ResizeObserver(scrollToSection);
    resizeObserver.observe(document.body);
    const timeout = window.setTimeout(() => stop(), SETTLE_MS);
    for (const event of USER_SCROLL_EVENTS) {
      window.addEventListener(event, stop, { passive: true });
    }
    function stop() {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      window.clearTimeout(timeout);
      for (const event of USER_SCROLL_EVENTS) {
        window.removeEventListener(event, stop);
      }
    }

    scrollToSection();
    return stop;
    // `key` re-runs this when the same hash is picked again.
  }, [hash, key]);
};
